'use client';

import { Copy, Gift, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@kit/ui/badge';
import { Button } from '@kit/ui/button';
import { CurrencyIcon } from '@kit/ui/currency-icon';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from '@kit/ui/drawer';
import { toast } from '@kit/ui/sonner';
import { cn } from '@kit/ui/utils';

import type { PublicDiscountCode } from '../_lib/public-discount-codes';
import { useBookingPresentation } from './booking-presentation';

/**
 * Small pill rendered on a package card advertising the public discount codes
 * that apply to it. Tapping opens the offers drawer (stopPropagation so the
 * card's own select handler doesn't fire).
 *
 * With exactly one code the pill shows the code itself — a "(1)" counter makes
 * the customer open a drawer to learn a single short string we could have just
 * told them. Two or more keeps the count, since the codes won't fit.
 */
export function PackageOffersBadge({
  offers,
  onClick,
  className,
}: {
  offers: PublicDiscountCode[];
  onClick: () => void;
  className?: string;
}) {
  const { t } = useTranslation('addons');

  if (offers.length === 0) return null;

  const soleOffer = offers.length === 1 ? offers[0] : undefined;

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800 transition-colors hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200 dark:hover:bg-amber-950/60',
        className,
      )}
      data-test="package-offers-badge"
    >
      <Gift className="h-3.5 w-3.5 shrink-0" />
      {soleOffer ? (
        <span className="font-mono font-semibold tracking-wider" dir="ltr">
          {soleOffer.code}
        </span>
      ) : (
        <>
          <span>{t('customerDiscount.offers.badgeLabel')}</span>
          <Badge className="h-4 min-w-4 rounded-full bg-amber-600 px-1 text-[10px] leading-none text-white hover:bg-amber-600">
            {offers.length}
          </Badge>
        </>
      )}
    </button>
  );
}

/**
 * Bottom drawer listing the public discount codes that apply to a package —
 * the "available offers" sheet. Each row shows the discount magnitude and the
 * code in a copyable chip; redemption itself happens in the checkout's
 * discount input (or automatically when only one code exists).
 */
export function PackageOffersDrawer({
  open,
  onOpenChange,
  packageName,
  offers,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  packageName: string;
  offers: PublicDiscountCode[];
}) {
  const presentation = useBookingPresentation();
  const { t } = useTranslation('addons');

  const copyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(t('customerDiscount.offers.copied', { code }));
    } catch {
      // Clipboard unavailable (older webviews) — the code is visible either way.
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        {...presentation}
        className={cn(
          'md:!right-[calc(50%-21rem)] md:!left-[calc(50%-21rem)]',
          presentation.className,
        )}
      >
        <div className="relative flex max-h-[85vh] flex-col">
          <DrawerClose asChild>
            <Button
              variant="ghost"
              size="icon"
              className="absolute end-3 top-3 z-10 h-9 w-9 rounded-full"
              aria-label={t('customerDiscount.close')}
            >
              <X className="h-4 w-4" />
            </Button>
          </DrawerClose>

          <div className="px-6 pe-14 pt-4 pb-2">
            <DrawerTitle className="flex items-center gap-2 text-xl font-semibold">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300">
                <Gift className="h-5 w-5" />
              </span>
              {t('customerDiscount.offers.title')}
            </DrawerTitle>
            <DrawerDescription dir="auto" className="mt-1">
              {t('customerDiscount.offers.subtitle', { name: packageName })}
            </DrawerDescription>
          </div>

          <div className="space-y-3 overflow-y-auto px-6 pb-6">
            {offers.map((offer) => (
              <div
                key={offer.id}
                className="flex items-center justify-between gap-3 rounded-xl border p-3"
                data-test="package-offer-row"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-12 min-w-12 flex-col items-center justify-center rounded-lg bg-amber-600 px-2 text-white">
                    {offer.discount_type === 'percent' ? (
                      <span className="text-sm font-bold">
                        {Number(offer.discount_value).toFixed(0)}%
                      </span>
                    ) : (
                      <span className="flex items-center gap-0.5 text-sm font-bold">
                        <CurrencyIcon className="h-3 w-3" />
                        {Number(offer.discount_value).toFixed(0)}
                      </span>
                    )}
                    <span className="text-[10px] leading-none opacity-90">
                      {t('customerDiscount.offers.off')}
                    </span>
                  </div>
                  <p className="text-sm font-medium" dir="auto">
                    {offer.name}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => void copyCode(offer.code)}
                  className="text-foreground hover:bg-muted/60 inline-flex items-center gap-1.5 rounded-md border border-dashed px-2.5 py-1.5 font-mono text-xs font-semibold tracking-wider transition-colors"
                  aria-label={t('customerDiscount.offers.copyAria', {
                    code: offer.code,
                  })}
                  data-test="package-offer-copy"
                >
                  {offer.code}
                  <Copy className="text-muted-foreground h-3 w-3" />
                </button>
              </div>
            ))}

            <p className="text-muted-foreground bg-muted/40 rounded-lg px-3 py-2 text-center text-xs">
              {t('customerDiscount.offers.applyHint')}
            </p>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
